const API_URL =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.PROD
    ? "https://vektor-markets-api.vektor-api.workers.dev/api"
    : "/api");
const token = () => sessionStorage.getItem("vektor-admin-token");
const timedFetch = (
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = 30_000,
) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(input, { ...init, signal: controller.signal }).finally(() =>
    clearTimeout(timeout),
  );
};
async function request<T>(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  if (token()) headers.set("Authorization", `Bearer ${token()}`);
  let response: Response;
  try {
    response = await timedFetch(`${API_URL}${path}`, {
      ...init,
      credentials: "include",
      headers,
    });
  } catch {
    throw new Error("Server bilan aloqa uzildi yoki so‘rov vaqti tugadi");
  }
  const body = (await response.json().catch(() => null)) as {
    data?: T;
    error?: { message?: string };
  } | null;
  if (!response.ok)
    throw new Error(body?.error?.message ?? "Server so‘rovi bajarilmadi");
  if (!body || !("data" in body))
    throw new Error("Server noto‘g‘ri javob qaytardi");
  return body.data as T;
}
export async function adminLogout() {
  try {
    await request<{ loggedOut: boolean }>("/auth/logout", { method: "POST" });
  } finally {
    sessionStorage.removeItem("vektor-admin-token");
  }
}
export async function adminLogin(username: string, password: string) {
  const data = await request<{
    sessionToken: string;
    username: string;
    role: AdminRole;
  }>("/auth/admin/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  sessionStorage.setItem("vektor-admin-token", data.sessionToken);
  return data;
}
export type AdminRole = "owner" | "finance" | "support" | "analyst";
export type AdminMe = {
  id: string;
  username: string;
  role: AdminRole;
  status: string;
  lastLoginAt?: string | null;
};
export const getAdminMe = () => request<AdminMe>("/admin/me");
export type AdminSearchResult = {
  id: string;
  type: "user" | "payment" | "analysis";
  title: string;
  subtitle: string;
  target: "users" | "payments" | "analyses";
};
export const searchAdmin = (query: string) =>
  request<AdminSearchResult[]>(`/admin/search?q=${encodeURIComponent(query)}`);

export type RiskControls = {
  signalsEnabled: boolean;
  minSignalConfidence: number;
  minRiskReward: number;
  dailySignalLimit: number;
  assetCooldownMinutes: number;
  signalsToday: number;
  recentSignals: Array<{
    id: string;
    symbol: string;
    direction: string;
    confidence: number;
    createdAt: string;
  }>;
};
export const getRiskControls = () =>
  request<RiskControls>("/admin/risk-controls");
export const updateRiskControls = (
  input: Omit<RiskControls, "signalsToday" | "recentSignals">,
) =>
  request<Omit<RiskControls, "signalsToday" | "recentSignals">>(
    "/admin/risk-controls",
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );

export type Revenue = {
  days: number;
  revenue: number;
  approvedCount: number;
  averageOrder: number;
  pendingAmount: number;
  pendingCount: number;
  expiringNext7Days: number;
  trialConversionRate: number;
  daily: Array<{ day: string; revenue: number; count: number }>;
  plans: Array<{ planCode: string; revenue: number; count: number }>;
};
export const getRevenue = (days: 30 | 90 | 365) =>
  request<Revenue>(`/admin/revenue?days=${days}`);

export type Broadcast = {
  id: string;
  status: string;
  segment: string;
  messageUz: string;
  messageRu: string;
  createdAt: string;
  scheduledAt: string | null;
  sentAt: string | null;
  recipientCount: number;
  successCount: number;
  failureCount: number;
};
export const getBroadcasts = () => request<Broadcast[]>("/admin/broadcasts");
export const createBroadcast = (input: {
  segment: string;
  messageUz: string;
  messageRu: string;
  scheduledAt?: string | null;
}) =>
  request<{ id: string; status: string }>("/admin/broadcasts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
export type BroadcastPreview = {
  segment: string;
  audience: { total: number; uz: number; ru: number };
  messageUz: string;
  messageRu: string;
};
export const getBroadcastPreview = (id: string) =>
  request<BroadcastPreview>(`/admin/broadcasts/${id}/preview`, {
    method: "POST",
  });
export const sendBroadcast = (id: string, expectedRecipients: number) =>
  request<{ id: string; status: string }>(`/admin/broadcasts/${id}/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ confirmation: "YUBORISH", expectedRecipients }),
  });
export const cancelBroadcast = (id: string) =>
  request<{ id: string; status: string }>(`/admin/broadcasts/${id}/cancel`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ confirmation: "BEKOR QILISH" }),
  });

export type AdminAlert = {
  id: string;
  severity: "info" | "warning" | "critical";
  category: string;
  title: string;
  message: string;
  status: "open" | "acknowledged";
  createdAt: string;
};
export const getAdminAlerts = () => request<AdminAlert[]>("/admin/alerts");
export const acknowledgeAlert = (id: string) =>
  request<{ id: string; status: string }>(`/admin/alerts/${id}/acknowledge`, {
    method: "PATCH",
  });
export const getAlertSettings = () =>
  request<{ telegramChatId: string }>("/admin/alerts/settings");
export const updateAlertSettings = (telegramChatId: string) =>
  request<{ telegramChatId: string }>("/admin/alerts/settings", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ telegramChatId }),
  });

export type AdminMarketStatus = {
  marketClosed: boolean;
  configuredProvider: string;
  summary: { healthy: number; degraded: number; down: number };
  rows: Array<{
    provider: string;
    symbol: string;
    timeframe: string;
    status: "healthy" | "degraded" | "down";
    lastCandleAt: string | null;
    latencyMs: number | null;
    missingCandles: number;
    consecutiveFailures: number;
    circuitOpenUntil: string | null;
    lastError: string | null;
    updatedAt: string;
  }>;
};
export type AdminScanner = {
  items: Array<{
    symbol: string;
    timeframe: string;
    status: string;
    direction: string | null;
    confidence: number;
    setupStrength: number;
    marketRegime: string | null;
    errorCode: string | null;
    scannedAt: string;
  }>;
  popularAssets: Array<{
    symbol: string;
    timeframe: string;
    followers: number;
  }>;
  popularStrategies: Array<{ code: string; followers: number }>;
  funnel: Array<{
    code: string;
    scanned: number;
    setups: number;
    signals: number;
    results: number;
  }>;
};
export const getAdminMarketStatus = () =>
  request<AdminMarketStatus>("/admin/market-status");
export const getAdminScanner = () => request<AdminScanner>("/admin/scanner");
export const runAdminScanner = (timeframe?: string) =>
  request<{
    timeframe: string;
    scannedAt: string;
    items: AdminScanner["items"];
  }>("/admin/scanner/run", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(timeframe ? { timeframe } : {}),
  });

export type AdminReferrals = {
  summary: {
    total: number;
    qualified: number;
    pending: number;
    rejected: number;
    rewardedAnalyses: number;
  };
  items: Array<{
    id: string;
    status: "pending" | "qualified" | "rejected";
    code: string;
    rewardAnalyses: number;
    fraudReason: string | null;
    createdAt: string;
    qualifiedAt: string | null;
    referrerName: string | null;
    referrerUsername: string | null;
    referredName: string | null;
    referredUsername: string | null;
  }>;
};
export const getAdminReferrals = () =>
  request<AdminReferrals>("/admin/referrals");
export const updateAdminReferral = (
  id: string,
  status: "pending" | "qualified" | "rejected",
  fraudReason: string | null = null,
) =>
  request(`/admin/referrals/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status, fraudReason }),
  });

export type AdminSupportTicket = {
  id: string;
  category: string;
  status: "open" | "waiting_user" | "in_progress" | "resolved" | "closed";
  priority: "low" | "normal" | "high" | "urgent";
  subject: string;
  assignedAdminId: string | null;
  lastMessageAt: string;
  createdAt: string;
  updatedAt: string;
  firstName: string | null;
  username: string | null;
  telegramId: string;
  lastMessage: string | null;
};
export type AdminSupportDetail = AdminSupportTicket & {
  messages: Array<{
    id: string;
    senderType: "user" | "admin";
    senderId: string;
    body: string;
    createdAt: string;
  }>;
};
export type SupportTemplate = {
  id: string;
  title: string;
  category: string;
  bodyUz: string;
  bodyRu: string;
};
export const getAdminSupportTickets = () =>
  request<AdminSupportTicket[]>("/admin/support/tickets");
export const getAdminSupportTicket = (id: string) =>
  request<AdminSupportDetail>(
    `/admin/support/tickets/${encodeURIComponent(id)}`,
  );
export const updateAdminSupportTicket = (
  id: string,
  status: AdminSupportTicket["status"],
  priority: AdminSupportTicket["priority"],
) =>
  request(`/admin/support/tickets/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status, priority }),
  });
export const replyAdminSupportTicket = (id: string, message: string) =>
  request(`/admin/support/tickets/${encodeURIComponent(id)}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message }),
  });
export const getSupportTemplates = () =>
  request<SupportTemplate[]>("/admin/support/templates");

export type AdminAccount = {
  id: string;
  username: string;
  role: AdminRole;
  status: "active" | "disabled";
  createdAt: string;
  lastLoginAt: string | null;
};
export const getAdminAccounts = () =>
  request<AdminAccount[]>("/admin/accounts");
export const createAdminAccount = (input: {
  username: string;
  password: string;
  role: AdminRole;
}) =>
  request<AdminAccount>("/admin/accounts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
export const updateAdminAccount = (
  id: string,
  input: { role: AdminRole; status: "active" | "disabled" },
) =>
  request<AdminAccount>(`/admin/accounts/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
export type PaymentSettings = {
  cardNumber: string;
  cardHolder: string;
  trial7Price: number;
  pro30Price: number;
  elite30Price: number;
  currency: "USD" | "UZS";
};
export const getPaymentSettings = () =>
  request<PaymentSettings>("/admin/payment-settings");
export const updatePaymentSettings = (settings: PaymentSettings) =>
  request<PaymentSettings>("/admin/payment-settings", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(settings),
  });
export type Dashboard = {
  users: number;
  activeSubscriptions: number;
  pendingPayments: number;
  monthlyAnalyses: number;
  liveSignals: number;
  plans: Array<{ code: string; count: number }>;
  marketProviderConfigured: boolean;
  aiConfigured: boolean;
};
export const getDashboard = () => request<Dashboard>("/admin/dashboard");
export type StrategyPerformanceMetric = {
  totalSignals: number;
  openSignals: number;
  closedSignals: number;
  wins: number;
  losses: number;
  breakeven: number;
  avgR: number;
  avgConfidence: number;
  winRate: number | null;
};
export type StrategyPerformance = {
  periodDays: 30 | 90 | 365;
  since: string;
  summary: StrategyPerformanceMetric;
  strategies: Array<
    StrategyPerformanceMetric & {
      id: string;
      code: string;
      nameUz: string;
      active: boolean;
    }
  >;
  recentResults: Array<{
    id: string;
    symbol: string;
    timeframe: string;
    strategyCode: string | null;
    resultType: "win" | "loss" | "breakeven";
    realizedR: number;
    closedAt: string;
  }>;
  dailySignals: Array<{
    day: string;
    total: number;
    tp: number;
    sl: number;
    breakeven: number;
    pending: number;
    expired: number;
  }>;
};
export const getStrategyPerformance = (days: 30 | 90 | 365) =>
  request<StrategyPerformance>(`/admin/strategy-performance?days=${days}`);
export type BacktestReport = {
  symbol:
    | "XAUUSD"
    | "EURUSD"
    | "GBPUSD"
    | "USDJPY"
    | "AUDUSD"
    | "USDCAD"
    | "USDCHF"
    | "NZDUSD";
  timeframe: "M5" | "M15" | "M30" | "H1" | "H4";
  strategy: string;
  provider: string;
  sampleCandles: number;
  trades: number;
  wins: number;
  losses: number;
  breakeven: number;
  expired: number;
  winRate: number | null;
  netR: number;
  expectancyR: number;
  profitFactor: number | null;
  maxDrawdownR: number;
  methodology: "walk_forward_conservative_v2";
  warningUz: string;
};
export const getBacktest = (
  symbol: string,
  timeframe: string,
  strategy: string,
) =>
  request<BacktestReport>(
    `/admin/backtest?${new URLSearchParams({ symbol, timeframe, strategy })}`,
  );
export type OperationsData = {
  health: {
    status: "success" | "partial" | "failed" | "running" | "stale" | "unknown";
    stale: boolean;
    expectedEveryMinutes: number;
    latestStartedAt: string | null;
  };
  runs: Array<{
    id: string;
    status: "success" | "partial" | "failed" | "running";
    startedAt: string;
    finishedAt: string | null;
    durationMs: number | null;
    trackedSignals: number;
    activatedSignals: number;
    closedSignals: number;
    createdSignals: number;
    expiredSignals: number;
    expiredSubscriptions: number;
    deletedSessions: number;
    errorCount: number;
    errors: string[];
  }>;
  signalStates: Array<{ status: string; count: number }>;
  eventCounts24h: Array<{ eventType: string; count: number }>;
  providerStats24h: Array<{
    provider: string;
    requests: number;
    avgLatencyMs: number;
    errors: number;
    failovers: number;
  }>;
  recentEvents: Array<{
    id: string;
    eventType: string;
    targetOrder: number | null;
    price: number | null;
    createdAt: string;
    symbol: string;
    timeframe: string;
    direction: "BUY" | "SELL";
  }>;
  clientErrors24h: { total: number; unresolved: number; occurrences: number };
  recentClientErrors: Array<{
    id: string;
    app: "miniapp" | "admin";
    route: string;
    message: string;
    occurrenceCount: number;
    firstSeenAt: string;
    lastSeenAt: string;
    resolvedAt: string | null;
  }>;
};
export const getOperations = () => request<OperationsData>("/admin/operations");
export const runOperationsCheck = () =>
  request<{ id: string; status: "success" | "partial" }>(
    "/admin/operations/run",
    { method: "POST" },
  );
export const resolveClientError = (id: string) =>
  request<{ id: string; resolvedAt: string }>(
    `/admin/operations/client-errors/${id}/resolve`,
    { method: "PATCH" },
  );
export type Payment = {
  id: string;
  username: string | null;
  first_name: string;
  amount: number;
  currency: string;
  plan_code: string;
  status: "pending" | "approved" | "rejected";
  created_at: string;
};
export const getPayments = () => request<Payment[]>("/admin/payments");
export const reviewPayment = (
  id: string,
  status: "approved" | "rejected",
  note?: string,
) =>
  request(`/admin/payments/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status, note }),
  });
export const receiptUrl = (id: string) =>
  `${API_URL}/admin/payments/${id}/receipt`;
export async function openReceipt(id: string) {
  const response = await fetch(receiptUrl(id), {
    credentials: "include",
    headers: token() ? { Authorization: `Bearer ${token()}` } : {},
  });
  if (!response.ok) throw new Error("Chekni ochib bo‘lmadi");
  const url = URL.createObjectURL(await response.blob());
  window.open(url, "_blank", "noopener,noreferrer");
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
export type User = {
  id: string;
  telegramId: number;
  username: string | null;
  firstName: string;
  status: string;
  createdAt: string;
  productCode: string | null;
  analysesUsed: number;
};
export const getUsers = () => request<User[]>("/admin/users");
export type UserDetail = {
  user: {
    id: string;
    telegramId: number;
    username: string | null;
    firstName: string;
    lastName: string | null;
    status: string;
    telegramLanguage: string | null;
    appLanguage: "uz" | "ru";
    createdAt: string;
    lastSeenAt: string;
  };
  access: {
    productCode: "FREE" | "TRIAL" | "PRO" | "ELITE";
    analysisLimit: number | null;
    analysesUsed: number;
    analysesRemaining: number | null;
    expiresAt: string | null;
    aiAssistant: boolean;
  };
  subscriptions: Array<{
    id: string;
    productCode: string;
    status: string;
    source: string;
    startsAt: string | null;
    expiresAt: string | null;
    createdAt: string;
  }>;
  payments: Array<{
    id: string;
    planCode: string;
    amount: number;
    currency: string;
    status: string;
    adminNote: string | null;
    reviewedAt: string | null;
    createdAt: string;
  }>;
  analyses: Array<{
    id: string;
    symbol: string;
    timeframe: string;
    status: string;
    provider: string;
    providerTimestamp: string | null;
    strategiesJson: string;
    createdAt: string;
  }>;
};
export const getUserDetail = (id: string) =>
  request<UserDetail>(`/admin/users/${encodeURIComponent(id)}`);
export const updateUserStatus = (id: string, status: "active" | "blocked") =>
  request<{ id: string; status: string }>(`/admin/users/${id}/status`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
export const grantSubscription = (
  id: string,
  productCode: "TRIAL" | "PRO" | "ELITE",
) =>
  request<{ productCode: string; expiresAt: string }>(
    `/admin/users/${id}/subscription`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ productCode }),
    },
  );
export const cancelSubscription = (id: string) =>
  request<{
    status: "cancelled";
    cancelledAt: string;
    cancelledCount: number;
  }>(`/admin/users/${encodeURIComponent(id)}/subscription/cancel`, {
    method: "POST",
  });
export type AuditLog = {
  id: string;
  adminId: string;
  action: string;
  entityType: string;
  entityId: string;
  beforeJson: string | null;
  afterJson: string | null;
  createdAt: string;
};
export const getAuditLogs = () => request<AuditLog[]>("/admin/audit");
export async function exportAuditCsv() {
  const response = await fetch(`${API_URL}/admin/audit/export`, {
    credentials: "include",
    headers: token() ? { Authorization: `Bearer ${token()}` } : {},
  });
  if (!response.ok) throw new Error("Audit eksport qilinmadi");
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `vektor-audit-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
export type Analysis = {
  id: string;
  username: string | null;
  firstName: string;
  symbol: string;
  timeframe: string;
  status: string;
  strategiesJson: string;
  providerTimestamp: string | null;
  createdAt: string;
};
export const getAnalyses = () => request<Analysis[]>("/admin/analyses");
export type PlatformSettings = {
  free_monthly_analyses: number;
  trial_total_analyses: number;
  pro_monthly_analyses: number;
  trial_days: number;
  pro_days: number;
  elite_days: number;
  news_blackout_minutes: number;
};
export const getPlatformSettings = () =>
  request<{
    settings: PlatformSettings;
    provider: { name: string; configured: boolean };
    ai: { name: string; configured: boolean };
  }>("/admin/platform-settings");
export const updatePlatformSettings = (settings: PlatformSettings) =>
  request<PlatformSettings>("/admin/platform-settings", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(settings),
  });
